import {FC} from 'react';
import {useIntl} from 'react-intl';
import {Box, Typography} from '@mui/material';
import {BoltOutlined} from '@mui/icons-material';
import dayjs from 'dayjs';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {convertMinutes} from '../../../../utils/helper';
import {StatusTag} from '../StatusTag';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {getEvDriverNameFromMinimal} from 'src/utils/userNameFormatter';
import {MinimalUserDTO, TransactionDTO} from 'src/types/transactions';
import {energyPillSx} from './ChargingSessionsTableCard.sx';

const getCompanyName = (user: MinimalUserDTO): string => {
    if (user?.userType === 'PRIVATE_DRIVER') {
        return '-';
    }
    if (user?.userType === 'COMPANY' && user?.company?.name) {
        return user.company.name;
    }
    if (user?.userType === 'EMPLOYEE' && user?.employee?.company?.name) {
        return user.employee.company.name;
    }
    return '-';
};

interface ChargingSessionsTableCardProps {
    data: TransactionDTO;
}

export const ChargingSessionsTableCard: FC<ChargingSessionsTableCardProps> = ({
                                                                                  data,
                                                                              }) => {
    const intl = useIntl();
    const {
        user,
        startTimestamp,
        stopTimestamp,
        transactionSummary,
        idTag,
        connector,
        location,
    } = data;
    const active = !stopTimestamp;
    const chargingTimeMinutes = transactionSummary?.chargingTimeMinutes || 0;
    const wattsConsumed = transactionSummary?.wattsConsumed || 0;
    // Which charger, and where it stands: the pair that tells two of one driver's sessions apart.
    const chargerLine = [connector?.chargePointId, location?.name].filter(Boolean).join(' · ');
    return (
        <TableCard>
            <PhoneCardHead
                icon={<BoltOutlined/>}
                title={getEvDriverNameFromMinimal(user)}
                subtitle={chargerLine}
            />
            {/* Whether it is still running, and what it has taken. */}
            <Box sx={phonePillsRowSx}>
                <StatusTag isActive={active}/>
                <Typography
                    component="span"
                    sx={energyPillSx}
                >
                    {`${(wattsConsumed / 1000).toFixed(2)} ${intl.formatMessage({id: 'kWh'})}`}
                </Typography>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'transactionStart'}),
                        value: dayjs(startTimestamp).format('DD MMM YYYY HH:mm:ss'),
                    },
                    {
                        label: intl.formatMessage({id: 'chargingTime'}),
                        value: convertMinutes(chargingTimeMinutes),
                    },
                    {
                        label: intl.formatMessage({id: 'company'}),
                        value: getCompanyName(user),
                    },
                    {
                        label: intl.formatMessage({id: 'rfid'}),
                        value: `${idTag?.idTag}`,
                    },
                ]}
            />
        </TableCard>
    );
};
